import { extendObservable, action } from 'mobx';
import firebase from 'firebase';
import User from '../models/User';
import Analytics from '../tools/Analytics';

const UserRef = firebase.database().ref("/users");

class UserStore {
  constructor() {
    extendObservable(this, {
      user: null,
    });
  }

  create = action((user, password) => {
    return firebase.auth().createUserWithEmailAndPassword(user.email, password)
          .then((_user) => {
            user.id = _user.uid;
            let data = {};
            data[user.id] = user.get();
            return UserRef.update(data);
          })
          .then(() => user);
  });

  delete = () => {
    return firebase.auth().currentUser.delete().then(this.logout);
  }

  login = action((email, password) => {
    return firebase.auth().signInWithEmailAndPassword(email, password);
  });

  reauthenticate = (password) => {
    const cred = firebase.auth.EmailAuthProvider.credential(this.user.email, password);
    return firebase.auth().currentUser.reauthenticateWithCredential(cred);
  }

  logout = action(() => {
    return firebase.auth().signOut();
  });

  update = (user) => {
    if (!this.isAuthenticated()) {
      return new Promise((resolve, reject) => reject());
    }
    let fbUser = firebase.auth().currentUser;
    return fbUser.updateProfile({
            displayName: user.name
          })
          .then(() => {
            let data = {};
            data[user.id] = user.get();
            return UserRef.update(data);
          })
          .then(action(() => this.user = user))
          .then(() => user);
  };

  waitFor = (key) => {
    return new Promise((resolve, reject) => {
      const ref = UserRef.child(`${this.user.id}/${key}`);
      const listener = ref.on("value", (snapshot) => {
        const value = snapshot.val();
        if (value) {
          ref.off("value", listener);
          let data = {};
          data[key] = value;
          this.user.set(data);
          resolve(value);
        }
      });
    });
  }

  changePassword = (password) => {
    if (!this.isAuthenticated) {
      return new Promise((resolve, reject) => reject());
    }
    return firebase.auth().currentUser.updatePassword(password);
  }

  isEmailVerified = () => {
    const user = firebase.auth().currentUser;
    return user.emailVerified;
  }

  sendEmailVerification = () => {
    if (!this.isAuthenticated()) {
      return new Promise((resolve, reject) => reject());
    }
    if (this.isEmailVerified()) {
      // already email verified
      return new Promise((resolve, reject) => resolve());
    }
    const user = firebase.auth().currentUser;
    return user.sendEmailVerification();
  }

  addPaymentMethod = (token) => {
    const data = {
      tokenId: token.id,
      stripeCustomerId: this.user.stripeCustomerId,
    };
    this.user.set({ stripeToken: data });
    return UserRef.child(`${this.user.id}/stripeError`).remove()
      .then(() => UserRef.child(`${this.user.id}/stripeToken`).set(data))
      .then(() => {
        return Promise.race([
          this.waitFor("stripeSource/fingerprint"),
          this.waitFor("stripeError").then((e) => { throw new Error(e.message); })
        ]);
      })
      .then(this.refresh)
      .then(() => {
        return this.user.stripeSource;
      });
  }

  deletePaymentMethod = () => {
    UserRef.child(`${this.user.id}/stripeSource`).remove()
    UserRef.child(`${this.user.id}/stripeToken`).remove();
    UserRef.child(`${this.user.id}/stripeError`).remove();
    this.user.set({ stripeSource: void 0 });
  }

  refresh = () => {
    return this.setUser(this.user.id);
  }

  setUser = (userId) => {
    return this.getUser(userId).then(action((user) => this.user = user))
    .then(user => {
      Analytics.setUser(user);
      return user;
    });
  };

  clearUser = action(() => {
    Analytics.clearUser();
    return this.user = null;
  });

  getUser = (userId => {
    return UserRef.child(userId).once("value")
            .then((snapshot) => new User(snapshot.val()));
  });

  isAuthenticated = () => {
    return this.user !== null;
  }

  forgotPassword = (email) => {
    return firebase.auth().sendPasswordResetEmail(email);
  }

  reloadFirebaseUserCache = () => {
    return firebase.auth().currentUser.reload();
  }
}

const userStore = new UserStore();

export default userStore;
export { UserStore }; // why?


// WEBPACK FOOTER //
// ./src/stores/UserStore.js